import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { roomLocationTooltip } from "../../../constants/TooltipInformation";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RoomLocation = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [roomLocationCode, setRoomLocationCode] = useState<string | null>(null);
  const [roomLocationCodeError, setRoomLocationCodeError] =
    useState<boolean>(false);
  const [roomLocationName, setRoomLocationName] = useState<string | null>(null);
  const [roomLocationNameError, setRoomLocationNameError] =
    useState<boolean>(false);
  const [roomLocationInformation, setRoomLocationInformation] =
    useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [active, setActive] = useState<boolean>(true);
  const [isCondo, setIsCondo] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const roomLocationPayload = {
    code: roomLocationCode,
    name: roomLocationName,
    isActive: active ?? true,
    isCondoOnly: isCondo ?? false,
  };

  const addRoomLocation = () => {
    if (validateInput()) {
      if (
        !roomLocationCode ||
        roomLocationCode === null ||
        roomLocationCode === ""
      ) {
        setRoomLocationCodeError(true);
      }
      if (
        !roomLocationName ||
        roomLocationName === null ||
        roomLocationName === ""
      ) {
        setRoomLocationNameError(true);
      } else if (
        checkDuplicate(
          Array.isArray(roomLocationInformation) ? roomLocationInformation : [],
          DNDFieldsEnum.ROOMLOCATIONCODE,
          roomLocationCode,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setRoomLocationInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...roomLocationPayload,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...roomLocationPayload,
                  },
                ],
          );
        });
        setLastUpdatedRecord(roomLocationPayload);
        editRoomLocation(null);
        setEditClicked(false);
      }
    }
  };

  const editRoomLocation = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setRoomLocationCode(list?.code);
    setRoomLocationName(list?.name);
    setIsCondo(list?.isCondoOnly);
    setActive(list ? list.isActive : true);
  };

  const updateRoomLocation = () => {
    if (!validateInput()) return;
    if (
      !roomLocationCode ||
      roomLocationCode === null ||
      roomLocationCode === ""
    ) {
      setRoomLocationCodeError(true);
      return;
    }
    if (
      !roomLocationName ||
      roomLocationName === null ||
      roomLocationName === ""
    ) {
      setRoomLocationNameError(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(roomLocationInformation)
          ? roomLocationInformation
          : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.ROOMLOCATIONCODE,
        roomLocationCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRoomLocationInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...roomLocationPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editRoomLocation(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.roomLocation} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteRoomLocation = (id: number) => {
    setRoomLocationInformation(deleteItem(roomLocationInformation, id));
  };

  const validateInput = () => {
    setRoomLocationCodeError(false);
    setRoomLocationNameError(false);
    let isValid = true;
    if (
      roomLocationCode &&
      !validateString(
        roomLocationCode,
        validationLength.roomTypeCode,
        validationType.SLASH_CODE,
      )
    ) {
      setRoomLocationCodeError(true);
      isValid = false;
    }
    if (
      roomLocationName &&
      !validateString(
        roomLocationName,
        validationLength.roomTypeName,
        validationType.OTHER_SLASH,
      )
    ) {
      setRoomLocationNameError(true);
      isValid = false;
    }
    return isValid;
  };

  useEffect(() => {
    data(roomLocationInformation);
    editClicked &&
      setSelectedId(
        checkData(roomLocationInformation, selectedId) ? selectedId : null,
      );
  }, [roomLocationInformation]);

  useEffect(() => {
    fieldValues &&
      setRoomLocationInformation(
        Array.isArray(fieldValues)
          ? fieldValues
          : fieldValues
            ? [fieldValues]
            : null,
      );
  }, []);

  useEffect(() => {
    validateInput();
  }, [roomLocationCode, roomLocationName]);

  const roomLocationCodeHelperText = roomLocationCodeError
    ? !(roomLocationCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.roomLocation.roomLocationCode
    : versaFieldHelperText.roomLocation.roomLocationCode;

  const roomLocationNameHelperText = roomLocationNameError
    ? !(roomLocationName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.roomLocation.roomLocationName
    : versaFieldHelperText.roomLocation.roomLocationName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Room Location Information
              <Tooltip title={tooltipComponent(roomLocationTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editRoomLocation(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRoomLocation(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Room Location Code
                <Tooltip
                  title={tooltipComponent(roomLocationTooltip.locationCode)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="roomLocationCode"
                variant="outlined"
                placeholder="Room Location Code"
                value={roomLocationCode ?? ""}
                error={roomLocationCodeError}
                sx={{ width: "100%" }}
                helperText={roomLocationCodeHelperText}
                onChange={(e: any) => {
                  setRoomLocationCode(e.target.value.toUpperCase());
                  setRoomLocationCodeError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Room Location Name
                <Tooltip
                  title={tooltipComponent(roomLocationTooltip.locationName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="roomLocationName"
                variant="outlined"
                placeholder="Room Location Name"
                value={roomLocationName ?? ""}
                error={roomLocationNameError}
                sx={{ width: "100%" }}
                helperText={roomLocationNameHelperText}
                onChange={(e: any) => {
                  setRoomLocationName(e.target.value);
                  setRoomLocationNameError(false);
                }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            width="100%"
            gap="50px"
          >
            <Stack direction="row" gap="2px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              {/* <Tooltip title={tooltipComponent("")}>
                <InfoIcon fontSize="small" />
              </Tooltip> */}
            </Stack>
            <Stack direction="row" gap="2px">
              <FormControlLabel
                control={
                  <Switch
                    id="CondoOnlySwitch2"
                    checked={isCondo}
                    onChange={() => setIsCondo(!isCondo)}
                  />
                }
                label="Condo Only?"
              />
              {/* <Tooltip title={tooltipComponent("")}>
                <InfoIcon fontSize="small" />
              </Tooltip> */}
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateRoomLocation : addRoomLocation}
              id="addNewRoomLocation"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Room
              Location
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Room Locations Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {roomLocationInformation ? (
                <DraggableItemList
                  itemData={roomLocationInformation}
                  onOrderUpdate={(order) => setRoomLocationInformation(order)}
                  onEdit={(info) => {
                    editRoomLocation(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRoomLocation(id)}
                  code={DNDFieldsEnum.ROOMLOCATIONCODE}
                  name={DNDFieldsEnum.ROOMLOCATIONNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Room Locations Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RoomLocation;
